import * as d3 from "d3"
import {
  get,
  flatten,
  join,
  merge,
  find,
  includes,
  reject,
  cloneDeep,
  isNull,
} from "lodash"
import { Feature, FeatureCollection } from "geojson"

import * as Formatter from "utils/Formatter"
import jp from "styles/image/icon/flags/jp.svg"
import us from "styles/image/icon/flags/us.svg"
import taiwan from "styles/image/icon/flags/taiwan.svg"
import philippines from "styles/image/icon/flags/philippines.svg"
import vietnam from "styles/image/icon/flags/vietnam.svg"
import indonesia from "styles/image/icon/flags/indonesia.svg"
import thailand from "styles/image/icon/flags/thailand.svg"
import malaysia from "styles/image/icon/flags/malaysia.svg"
import singapore from "styles/image/icon/flags/singapore.svg"
import china from "styles/image/icon/flags/china.svg"
import hongKong from "styles/image/icon/flags/hong-kong.svg"
import korea from "styles/image/icon/flags/korea.svg"
import israel from "styles/image/icon/flags/israel.svg"
import { APIResponse } from "redux/apis/types"

type MarketDataType = {
  id: number
  SymbolName: string
  Price: number
  ChangedRatio: number
  ChangedPrice: number
  PriceTime: string
}

//レーダーチャート

export type RadarDataType = {
  id: number
  name: string
  volume: number
}

export class RadarChart {
  width: number | null = null
  height: number | null = null
  // color: d3.ScaleOrdinal<string, unknown>
  config = {
    radius: 5,
    width: 300,
    height: 300,
    factor: 1,
    factorLegend: 0.85,
    levels: 5,
    maxValue: 0,
    radians: 2 * Math.PI,
    opacityArea: 0.5,
    ToRight: 5,
    TranslateX: 8,
    TranslateY: 20,
    ExtraWidthX: 100,
    ExtraWidthY: 100,
    color: ["#6F257F", "#CA0D59"],
  }
  body: d3.Selection<HTMLDivElement, unknown, HTMLElement, any>
  svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any> | null = null
  data: Array<Array<RadarDataType>>
  totalItems: number
  allAxis: Array<string>
  radius: number

  g: d3.Selection<SVGGElement, unknown, HTMLElement, any>
  axis: d3.Selection<SVGGElement, string, SVGGElement, unknown>

  constructor(data: Array<Array<RadarDataType>>, w?: number, h?: number) {
    if (w && h) {
      this.config.width = w
      this.config.height = h
    }
    //ここがoptionで動かせるよう修正
    this.config.maxValue = 10
    this.data = data
    this.body = d3.select(".radar")
    if (!this.body) return null
    this.setParams()
    this.draw()
    this.drawAxis()
    this.drawRadar()
    this.drawArea()
  }

  setParams() {
    // this.color = d3.scaleOrdinal().domain(this.config.color.map((d, i) => `${i}`)).range(this.config.color).unknown("#fff")
    this.svg = this.body
      .append("svg")
      .attr("width", this.config.width)
      .attr("height", this.config.height)
    this.totalItems = this.data[0].length
    this.allAxis = this.data[0].map(data => data.name)
    this.radius =
      this.config.factor *
      Math.min(this.config.width / 2, this.config.height / 2)
  }

  draw() {
    if (!this.svg) return
    this.g = this.svg
      .append("g")
      .attr(
        "transform",
        `translate(${this.config.TranslateX}, ${this.config.TranslateY})`
      )
  }

  drawAxis() {
    this.axis = this.g
      .selectAll(".axis")
      .data(this.allAxis)
      .enter()
      .append("g")
      .attr("class", "axis")

    this.axis
      .append("line")
      .attr("x1", this.config.width / 2)
      .attr("y1", this.config.height / 2)
      .attr(
        "x2",
        (d, i) =>
          (this.config.width / 2) *
          (1 -
            this.config.factor *
              Math.sin((i * this.config.radians) / this.totalItems))
      )
      .attr(
        "y2",
        (d, i) =>
          (this.config.height / 2) *
          (1 -
            this.config.factor *
              Math.cos((i * this.config.radians) / this.totalItems))
      )
      .attr("class", "line")
      .attr("stroke", "grey")
      .attr("stroke-width", "1px")

    this.axis
      .append("text")
      .attr("class", "legend")
      .text((d: string) => d)
      .style("font-family", "sans-serif")
      .style("font-size", "11px")
      .attr("text-anchor", "middle")
      .attr("dy", "1.5em")
      .attr("transform", `translate(0, -10)`)
      .attr(
        "x",
        (d, i) =>
          (this.config.width / 2) *
            (1 -
              this.config.factorLegend *
                Math.sin((i * this.config.radians) / this.totalItems)) -
          20 * Math.sin((i * this.config.radians) / this.totalItems)
      )
      .attr(
        "y",
        (d, i) =>
          (this.config.height / 2) *
            (1 -
              this.config.factorLegend *
                Math.cos((i * this.config.radians) / this.totalItems)) -
          20 * Math.cos((i * this.config.radians) / this.totalItems)
      )
  }

  drawRadar() {
    for (let i = 0; i < this.config.levels; i++) {
      const levelFactor =
        this.config.factor * this.radius * ((i + 1) / this.config.levels)
      this.g
        .selectAll(".levels")
        .data(this.allAxis)
        .enter()
        .append("svg:line")
        .attr(
          "x1",
          (d, j) =>
            levelFactor *
            (1 -
              this.config.factor *
                Math.sin((j * this.config.radians) / this.totalItems))
        )
        .attr(
          "y1",
          (d, j) =>
            levelFactor *
            (1 -
              this.config.factor *
                Math.cos((j * this.config.radians) / this.totalItems))
        )
        .attr(
          "x2",
          (d, j) =>
            levelFactor *
            (1 -
              this.config.factor *
                Math.sin(((j + 1) * this.config.radians) / this.totalItems))
        )
        .attr(
          "y2",
          (d, j) =>
            levelFactor *
            (1 -
              this.config.factor *
                Math.cos(((j + 1) * this.config.radians) / this.totalItems))
        )
        .attr("class", "line")
        .attr("stroke", "grey")
        .attr("stroke-opacity", 0.75)
        .attr("stroke-width", "0.3px")
        .attr(
          "transform",
          `translate(${this.config.width / 2 - levelFactor}, ${this.config
            .height /
            2 -
            levelFactor})`
        )

      this.g
        .selectAll(".levels")
        .data(this.data[0])
        .enter()
        .append("svg:text")
        .attr("x", () => levelFactor * (1 - this.config.factor * Math.sin(0)))
        .attr("y", () => levelFactor * (1 - this.config.factor * Math.cos(0)))
        .attr("class", "legend")
        .attr("font-family", "sans-serif")
        .attr("font-size", "13px")
        .attr(
          "transform",
          `translate(${this.config.width / 2 -
            levelFactor +
            this.config.ToRight},${this.config.height / 2 - levelFactor})`
        )
        .attr("fill", "#737373")
        .text(((i + 1) * this.config.maxValue) / this.config.levels)
    }
  }

  drawArea() {
    const dataValues: Array<Array<[number, number]>> = this.data.map(data => {
      const dataArray: Array<[number, number]> = data.map((d, j) => {
        const dataVolume: number = get(d, "volume", null)
        return [
          (this.config.width / 2) *
            (1 -
              (parseFloat(String(Math.max(dataVolume, 0))) /
                this.config.maxValue) *
                this.config.factor *
                Math.sin((j * this.config.radians) / this.totalItems)),
          (this.config.height / 2) *
            (1 -
              (parseFloat(String(Math.max(dataVolume, 0))) /
                this.config.maxValue) *
                this.config.factor *
                Math.cos((j * this.config.radians) / this.totalItems)),
        ]
      })
      dataArray.push(dataArray[0])
      return dataArray
    })

    this.g
      .append("g")
      .attr("class", "radar-chart-area")
      .selectAll(".area")
      .data(dataValues)
      .enter()
      .append("polygon")
      .attr("class", "radar-chart-polygon")
      .style("stroke-width", "2px")
      .style("stroke", (d, i: number) => this.config.color[i])
      .attr("points", d => join(flatten(d), ","))
      .style("fill", (d, i) => this.config.color[i])
      .style("fill-opacity", this.config.opacityArea)
      .on("mouseover", () => {
        this.g.selectAll("polygon").style("fill-opacity", 0.7)
      })
      .on("mouseout", () => {
        this.g
          .selectAll("polygon")
          .style("fill-opacity", this.config.opacityArea)
      })
  }
}

// 世界地図

export type PlotPositionsType = {
  id: number
  country: string
  cls: string
  position: [number, number]
  icon: string
  code: string
  index: Array<string>
  translate: [number, number]
  time: number
}

const dayTime = [8, 19]
//start, endどちらも[月、何週目]をいれる
type GetTimeZoneType = (
  start: [number, number],
  end: [number, number],
  summerTime: number
) => number
const getTimezone: GetTimeZoneType = (start, end, summerTime) => {
  const today = new Date(2019, 10, 3)
  const month = today.getMonth() + 1
  if (month < start[0] || end[0] < month) {
    return summerTime - 1
  } else if (start[0] < month && month < end[0]) {
    return summerTime
  } else {
    const todayDay = today.getDate()
    const getDay: (setting: [number, number], additional?: number) => number = (
      setting,
      additional = 0
    ) => {
      const startMonth = new Date(today.getFullYear(), setting[0] - 1)
      const firstSunday = !startMonth.getDay() ? 1 : 8 - startMonth.getDay()
      return firstSunday + (setting[1] - 1) * 7 + additional
    }
    if (month === start[0]) {
      const startDay = getDay(start, 1)
      return todayDay < startDay ? summerTime - 1 : summerTime
    } else {
      const endDay = getDay(end)
      return todayDay > endDay ? summerTime - 1 : summerTime
    }
  }
}

export const plotPositions: Array<PlotPositionsType> = [
  {
    id: 0,
    country: "日本",
    cls: "jp",
    position: [138, 38],
    icon: jp,
    code: "",
    index: ["101"],
    translate: [28, -72],
    time: 0,
  },
  {
    id: 1,
    country: "台湾",
    cls: "taiwan",
    position: [121.31, 25.2],
    icon: taiwan,
    code: "TW",
    index: ["TWII"],
    translate: [68, -60],
    time: -1,
  },
  {
    id: 2,
    country: "フィリピン",
    cls: "philippines",
    position: [120.58, 14.36],
    icon: philippines,
    code: "PH",
    index: ["PSI"],
    translate: [60, -42],
    time: -1,
  },
  {
    id: 3,
    country: "ベトナム",
    cls: "vietnam",
    position: [106.37, 10.49],
    icon: vietnam,
    code: "VN",
    index: ["VNI"],
    translate: [120, -2],
    time: -2,
  },
  {
    id: 4,
    country: "インドネシア",
    cls: "indonesia",
    position: [106.84, -6.2],
    icon: indonesia,
    code: "ID",
    index: ["JKSE"],
    translate: [0, -2],
    time: -2,
  },
  {
    id: 5,
    country: "タイ",
    cls: "thailand",
    position: [100.5, 13.75],
    icon: thailand,
    code: "TH",
    index: ["SETI"],
    translate: [-436, -52],
    time: -2,
  },
  {
    id: 6,
    country: "マレーシア",
    cls: "malaysia",
    position: [99.97, 7.21],
    icon: malaysia,
    code: "MY",
    index: ["KLSE"],
    translate: [-326, -20],
    time: -1,
  },
  {
    id: 7,
    country: "シンガポール",
    cls: "singapore",
    position: [103.81, 1.35],
    icon: singapore,
    code: "SG",
    index: ["STI"],
    translate: [-286, 16],
    time: -1,
  },
  {
    id: 8,
    country: "中国",
    cls: "china",
    position: [121.27, 31.13],
    icon: china,
    code: "CN",
    index: ["SSEC", "SZSC"],
    translate: [-300, -120],
    time: -1,
  },
  {
    id: 9,
    country: "香港",
    cls: "hong-kong",
    position: [114.16, 22.31],
    icon: hongKong,
    code: "HK",
    index: ["HSI"],
    translate: [-260, -28],
    time: -1,
  },
  {
    id: 10,
    country: "韓国",
    cls: "korea",
    position: [126.59, 37.34],
    icon: korea,
    code: "KR",
    index: ["KS11"],
    translate: [-128, -72],
    time: 0,
  },
  {
    id: 11,
    country: "イスラエル",
    cls: "israel",
    position: [35.21, 31.76],
    icon: israel,
    code: "IL",
    index: ["TA35"],
    translate: [-228, -74],
    time: -6,
  },
  {
    id: 12,
    country: "米国",
    cls: "us",
    position: [-74, 40.71],
    icon: us,
    code: "US",
    index: ["DJI", "INX", "IXIC"],
    translate: [-80, 30],
    time: getTimezone([3, 2], [11, 1], -13),
  },
]

type WorldMapOption = {
  width?: number
  height?: number
  center?: [number, number]
  subSize?: [number, number]
}

type D3Transform = {
  k: number
  x: number
  y: number
}

// 現状南極を消しています
const removeCountryAreaIndex = [159]
const minWindowWidth = 1040,
  maxWindowWidth = 1940

type DarkModeSetting = string
type BrightModeSetting = string
const colorSetting: { [k: string]: [DarkModeSetting, BrightModeSetting] } = {
  mapFill: ["#404040", "#cad5e4"],
  mapStroke: ["#153034", "#014569"],
  strokeLine: ["#016f9b", "#016f9b"],
  boxStroke: ["#016f9b", "#014569"],
  boxFill: ["#000", "#000"],
  fontColor: ["#fff", "#fff"],
  //点線のとこの色
  dashColor: ["#fff", "#fff"],
  up: ["#ff4d8b", "#ff4d8b"],
  down: ["#3aa7ff", "#3aa7ff"],
}

const chartFont = "Robot"
const baseFontSize = "11px"
const timeFontSize = "10px"

//点線になっているとこです。直線は0に設定してください。
const strokeDashArray = "2"

export class WorldMap {
  data: FeatureCollection
  api: APIResponse.Res
  svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any> | null = null
  map: d3.Selection<SVGGElement, unknown, HTMLElement, any>
  mapArea: d3.Selection<any, any, any, any>
  countries: d3.Selection<any, any, any, any>
  marketArea: d3.Selection<any, any, any, any>
  fn: Function
  projection: d3.GeoProjection
  isDarkMode: boolean
  hover: number = null
  config: WorldMapOption = {
    width: null,
    height: null,
    center: [124, 12],
    subSize: [200, 160],
  }
  width: number
  height: number
  transform: D3Transform = {
    k: 1,
    x: 0,
    y: -40,
  }
  constructor(
    data: FeatureCollection,
    container: HTMLDivElement,
    api: APIResponse.Res,
    fn: Function,
    option?: WorldMapOption
  ) {
    if (!data) return
    const htmlClass = document.querySelector("html").classList
    this.isDarkMode = includes(htmlClass, "dark")
    this.config = merge(this.config, option)
    this.api = api
    this.data = data
    this.data.features = reject(this.data.features, (d: Feature, i: number) =>
      includes(removeCountryAreaIndex, i)
    )
    this.fn = fn
    this.setParams(container)
    this.draw()
    this.event()
  }

  setParams(container: HTMLDivElement) {
    this.svg = d3.select(container).append("svg")
  }

  draw() {
    const windowWidth = window.innerWidth
    const settingWindowWidth =
      windowWidth > maxWindowWidth
        ? maxWindowWidth
        : windowWidth > minWindowWidth
        ? windowWidth
        : minWindowWidth
    const scaleRate = settingWindowWidth / minWindowWidth
    const windowRate = 1 + (scaleRate - 1) / 2
    const translateWindowRate = 1 + (scaleRate - 1) / 3
    this.width = this.config.width ? this.config.width : window.innerWidth
    this.height = this.config.height
      ? this.config.height
      : window.innerHeight - 180
    this.svg.attr("width", this.width).attr("height", this.height * windowRate)
    this.projection = d3
      .geoMercator()
      .center([0, 51])
      .fitExtent([[0, 0], [this.width - 400, this.height]], this.data)
      .scale(180 * scaleRate)
      .translate([
        (580 / maxWindowWidth) * settingWindowWidth * translateWindowRate,
        160 - (170 / maxWindowWidth) * settingWindowWidth,
      ])
    this.map = this.svg
      .append("g")
      .attr(
        "transform",
        `translate(${this.transform.x} ${this.transform.y}) scale(${this.transform.k})`
      )
    this.drawMap()
    this.drawCountryInfo()
    this.drawMarketArea()
  }

  redraw() {
    this.svg.selectAll("*").remove()
    const htmlClass = document.querySelector("html").classList
    this.isDarkMode = includes(htmlClass, "dark")
    this.draw()
  }

  drawMap() {
    const path = d3.geoPath().projection(this.projection)
    this.mapArea = this.map
      .append("g")
      .selectAll("path")
      .data(this.data.features)
      .enter()
      .append("g")
      .append("path")
      .attr("d", path)
      .attr("fill", colorSetting.mapFill[this.isDarkMode ? 0 : 1])
      .attr("stroke", colorSetting.mapStroke[this.isDarkMode ? 0 : 1])
  }

  drawCountryInfo() {
    this.countries = this.map.selectAll("circle").append("g")
    const hour = new Date().getHours()

    const iconSize = 18

    this.countries
      .data(plotPositions)
      .enter()
      .append("line")
      .attr(
        "x1",
        (datum: PlotPositionsType) =>
          this.projection(datum.position)[0] + iconSize / 2
      )
      .attr(
        "y1",
        (datum: PlotPositionsType) =>
          this.projection(datum.position)[1] + iconSize / 2
      )
      .attr("x2", (datum: PlotPositionsType) => {
        return this.projection(datum.position)[0] + 30 + datum.translate[0]
      })
      .attr("y2", (datum: PlotPositionsType) => {
        return this.projection(datum.position)[1] + 20 + datum.translate[1]
      })
      .attr("stroke", colorSetting.strokeLine[this.isDarkMode ? 0 : 1])
      .attr("stroke-width", 1.5)

    this.countries
      .data(plotPositions)
      .enter()
      .append("image")
      .attr(
        "x",
        (datum: PlotPositionsType) => this.projection(datum.position)[0]
      )
      .attr(
        "y",
        (datum: PlotPositionsType) => this.projection(datum.position)[1]
      )
      .attr("width", iconSize)
      .attr("height", iconSize)
      .attr("xlink:href", d => d.icon)
      .attr("opacity", d => {
        const h = hour + d.time
        return dayTime[0] <= h && h < dayTime[1] ? 1 : 0.6
      })
      .on("click", d => this.fn(d))
      .style("cursor", "pointer")
  }

  drawMarketArea() {
    const infoAreaX = 184,
      infoAreaY = [50, 36]

    const getDataIndex: (data: APIResponse.Res) => number = data => {
      let index: number = null
      plotPositions.forEach(li => {
        const resIndex = li.index.indexOf(get(data, "Symbol"))
        if (resIndex >= 0) index = resIndex
      })
      return index + 1
    }

    const getPosition = (datum: APIResponse.Res) =>
      find(plotPositions, li => includes(li.index, get(datum, "Symbol")))

    this.marketArea = this.countries
      .data(get(this.api, "ResultList", []))
      .enter()
      .append("g")
      .attr("transform", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return `translate(${position.translate[0]},${position.translate[1]})`
      })
      .attr("class", "map-market-area")
      .style("cursor", "pointer")

    this.marketArea
      .append("rect")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.projection(position.position)[0] + 30
      })
      .attr("y", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          15 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .attr("width", infoAreaX)
      .attr("height", datum => {
        const position = getPosition(datum)
        return infoAreaY[this.hover === position.id ? 0 : 1]
      })
      .attr("stroke", colorSetting.boxStroke[this.isDarkMode ? 0 : 1])

    this.marketArea
      .append("line")
      .attr("x1", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.projection(position.position)[0] + 30
      })
      .attr("y1", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          20 +
          12 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .attr("x2", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.projection(position.position)[0] + infoAreaX + 30
      })
      .attr("y2", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          20 +
          12 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .attr("stroke", colorSetting.dashColor[this.isDarkMode ? 0 : 1])
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", strokeDashArray)

    this.marketArea
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.projection(position.position)[0] + 6 + 30
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          27 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return !index ? position.country : ""
      })
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", colorSetting.fontColor[this.isDarkMode ? 0 : 1])

    this.marketArea
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.projection(position.position)[0] + infoAreaX - 36 + 30
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          27 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => {
        return Formatter.date(get(datum, "PriceTime"), "HH:mm")
      })
      .attr("font-family", chartFont)
      .attr("font-size", timeFontSize)
      .attr("fill", colorSetting.fontColor[this.isDarkMode ? 0 : 1])

    this.marketArea
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.projection(position.position)[0] + 6 + 30
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          46 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => get(datum, "SymbolName"))
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", colorSetting.fontColor[this.isDarkMode ? 0 : 1])

    this.marketArea
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const price = Formatter.number(
          get(datum, "Price"),
          get(datum, "NumberOfDecimals")
        )

        const fontWidth = String(price).length * 3.5
        return this.projection(position.position)[0] + infoAreaX - fontWidth
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          46 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) =>
        Formatter.number(get(datum, "Price"), get(datum, "NumberOfDecimals"))
      )
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", colorSetting.fontColor[this.isDarkMode ? 0 : 1])

    this.marketArea // 前日比
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const price = Formatter.number(get(datum, "ChangedPrice"), 2)
        const signPrice = get(datum, "ChangedPrice") ? "+".length * 6 : 0
        const ratio = Formatter.number(get(datum, "ChangedRatio"), 2)
        const signRatio = get(datum, "ChangedRatio") ? "+".length * 6 : 0
        const fontWidth =
          signPrice +
          String(price).length * 6 +
          "(".length * 6 +
          signRatio +
          String(ratio).length * 6
        return this.projection(position.position)[0] + infoAreaX - fontWidth - 2
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          infoAreaY[this.hover === position.id ? 0 : 1] -
          4 +
          14 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.hover === position.id
          ? Formatter.number(get(datum, "ChangedPrice"), 2, "floor", true)
          : null
      })
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", (datum: APIResponse.Res) => {
        const ratio = get(datum, "ChangedRatio", null)
        if (ratio === 0) return colorSetting.fontColor[this.isDarkMode ? 0 : 1]
        else if (ratio > 0) return colorSetting.up[this.isDarkMode ? 0 : 1]
        else if (ratio < 0) return colorSetting.down[this.isDarkMode ? 0 : 1]
      })
    this.marketArea // (
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const ratio = Formatter.number(get(datum, "ChangedRatio"), 2)
        const sign = get(datum, "ChangedRatio") ? "+".length * 6 : 0
        const fontWidth =
          ("(".length * 6 + sign + String(ratio).length * 6 + "%)".length * 6) /
          2
        return this.projection(position.position)[0] + infoAreaX - fontWidth - 6
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          infoAreaY[this.hover === position.id ? 0 : 1] -
          4 +
          14 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.hover === position.id && !isNull(get(datum, "ChangedRatio"))
          ? "("
          : null
      })
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", colorSetting.fontColor[this.isDarkMode ? 0 : 1])
    this.marketArea // 騰落率
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const ratio = Formatter.number(get(datum, "ChangedRatio"), 2)
        const sign = get(datum, "ChangedRatio") ? "+".length * 6 : 0
        const fontWidth =
          (sign + String(ratio).length * 6 + "%)".length * 6) / 2
        return this.projection(position.position)[0] + infoAreaX - fontWidth - 4
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          infoAreaY[this.hover === position.id ? 0 : 1] -
          4 +
          14 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.hover === position.id
          ? Formatter.number(get(datum, "ChangedRatio"), 2, "floor", true)
          : null
      })
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", (datum: APIResponse.Res) => {
        const ratio = get(datum, "ChangedRatio", null)
        if (ratio === 0) return colorSetting.fontColor[this.isDarkMode ? 0 : 1]
        else if (ratio > 0) return colorSetting.up[this.isDarkMode ? 0 : 1]
        else if (ratio < 0) return colorSetting.down[this.isDarkMode ? 0 : 1]
      })
    this.marketArea // %)
      .append("text")
      .attr("x", (datum: APIResponse.Res) => {
        const position = getPosition(datum)
        const fontWidth = "%)".length * 6
        return this.projection(position.position)[0] + infoAreaX + fontWidth - 2
      })
      .attr("y", (datum: APIResponse.Res, i) => {
        const position = getPosition(datum)
        const index = getDataIndex(datum) - 1
        return (
          this.projection(position.position)[1] +
          infoAreaY[this.hover === position.id ? 0 : 1] -
          4 +
          14 +
          infoAreaY[this.hover === position.id ? 0 : 1] * index
        )
      })
      .text((datum: APIResponse.Res) => {
        const position = getPosition(datum)
        return this.hover === position.id && !isNull(get(datum, "ChangedRatio"))
          ? "%)"
          : null
      })
      .attr("font-family", chartFont)
      .attr("font-size", baseFontSize)
      .attr("fill", colorSetting.fontColor[this.isDarkMode ? 0 : 1])

    //event
    this.marketArea.on("click", (d, i) => {
      let index: number
      const position = find(plotPositions, li => {
        if (includes(li.index, get(d, "Symbol"))) {
          index = li.index.indexOf(get(d, "Symbol"))
          return true
        }
      })
      this.fn(position, index)
    })
    this.marketArea.on("mousemove", datum => {
      const position = getPosition(datum)
      if (this.hover !== position.id) {
        this.hover = position.id
        this.marketArea.remove()
        this.drawMarketArea()
      }
    })
    this.marketArea.on("mouseleave", () => {
      if (this.hover !== null) {
        this.hover = null
        this.marketArea.remove()
        this.drawMarketArea()
      }
    })
  }

  event() {
    window.addEventListener("resize", () => this.redraw())
  }
}

// 棒グラフ

export type BarChartDataType = Array<{
  name: string
  volume: number
}>

export type BarChartOptionType = {
  width?: number
  height?: number
  padding?: [number, number] // [x, y]
  color?: string | Array<string>
}

export const defaultChartConfig: BarChartOptionType = {
  width: 400,
  height: 400,
  padding: [30, 30],
  color: "#5cb2d1",
}

export class BarChart {
  data: BarChartDataType
  target: HTMLElement
  title: string
  svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any> | null = null
  noneLabelDiagonal: boolean

  config = cloneDeep(defaultChartConfig)

  constructor(
    data: BarChartDataType,
    target: HTMLElement,
    title?: string,
    option?: BarChartOptionType,
    noneLabelDiagonal?: boolean
  ) {
    this.data = data
    this.target = target
    this.title = !!title ? title : null
    this.config = !!option ? merge(this.config, option) : this.config
    this.noneLabelDiagonal = !!noneLabelDiagonal
    this.setParams()
    this.draw()
  }

  setParams() {
    this.svg = d3
      .select(this.target)
      .append("svg")
      .attr("width", this.config.width)
      .attr("height", this.config.height)
  }

  draw() {
    const htmlClass = document.querySelector("html").classList
    const isDarkMode = includes(htmlClass, "dark")

    const xScale = d3
      .scaleBand()
      .rangeRound([this.config.padding[0], this.config.width])
      .padding(0.1)
      .domain(this.data.map(d => d.name))

    const yScale = d3
      .scaleLinear()
      .domain([
        d3.min(this.data, d => (d.volume >= 0 ? 0 : d.volume)),
        d3.max(this.data, d => d.volume),
      ])
      .range([
        this.config.height - this.config.padding[1],
        this.config.padding[1],
      ])

    this.svg
      .append("g")
      .attr(
        "transform",
        `translate(0, ${this.config.height - this.config.padding[1] - 20})`
      )
      .call(d3.axisBottom(xScale))
      .classed("x-scale", true)

    if (!this.noneLabelDiagonal)
      this.svg
        .selectAll(".x-scale text")
        .attr("transform", `translate(-10, 15)rotate(-45)`)

    this.svg
      .append("g")
      .attr("transform", `translate(${this.config.padding[0]}, -20)`)
      .call(d3.axisLeft(yScale).ticks(5))

    this.svg
      .append("g")
      .selectAll("rect")
      .data(this.data)
      .enter()
      .append("rect")
      .attr("x", d => xScale(d.name))
      .attr("y", d => (d.volume ? yScale(d.volume) : 0))
      .attr("width", xScale.bandwidth())
      .attr("height", d => {
        const height =
          this.config.height - this.config.padding[1] - 20 - yScale(d.volume)
        if (!height && height !== 0) return 0
        return height
      })
      .attr("fill", (d, i) =>
        typeof this.config.color === "object"
          ? this.config.color[i % this.config.color.length]
          : this.config.color
      )

    this.svg
      .append("g")
      .attr("transform", `translate(${this.config.width / 2}, 12)`)
      .append("text")
      .attr("font-size", "14px")
      .attr("text-anchor", "middle")
      .attr("font-family", chartFont)
      .attr("fill", isDarkMode ? "#ffffff" : "#000000")
      .text(this.title)
  }

  redraw(option?: BarChartOptionType, data?: BarChartDataType, title?: string) {
    this.config = !!option ? merge(this.config, option) : this.config
    if (!!data) this.data = data
    if (!!title) this.title = title
    this.svg.remove()
    this.setParams()
    this.draw()
  }
}
